import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../hooks/useAuth';
import { cajaService } from '../../services/cajaService';
import './ArqueoDistribucion.css';

const ArqueoDistribucion = ({ onClose, sesionActual }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [analysis, setAnalysis] = useState(null);
    const [modo, setModo] = useState('EFECTIVO'); // 'EFECTIVO' or 'TODO'
    const [deposits, setDeposits] = useState({});
    const [processing, setProcessing] = useState(false);
    const [showFilters, setShowFilters] = useState(() => {
        const saved = localStorage.getItem('arqueo_showFilters');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [viewType, setViewType] = useState(() => {
        return localStorage.getItem('arqueo_viewType') || 'GRID';
    });

    const [depositModal, setDepositModal] = useState({
        isOpen: false,
        group: null,
        amount: '',
        accountId: ''
    });

    const [detailsModal, setDetailsModal] = useState({
        isOpen: false,
        group: null,
        movimientos: []
    });

    // Regularización states
    const [isRegModalOpen, setIsRegModalOpen] = useState(false);
    const [regBreakdown, setRegBreakdown] = useState({});
    const [regExpenses, setRegExpenses] = useState({}); // New: Mapping expenseId -> conceptKey
    const [regTargetId, setRegTargetId] = useState(null);
    const [regTotalDisponible, setRegTotalDisponible] = useState(0);

    // Estado para modal dedicado de gestión de gastos
    const [isGastosModalOpen, setIsGastosModalOpen] = useState(false);
    const [searchGastos, setSearchGastos] = useState('');
    const [directExpenseAssign, setDirectExpenseAssign] = useState({}); // { egresoId: conceptKey }

    // Filter states with localStorage persistence
    const [hideTransferred, setHideTransferred] = useState(() => {
        const saved = localStorage.getItem('arqueo_hideTransferred');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [hideZeroBalance, setHideZeroBalance] = useState(() => {
        const saved = localStorage.getItem('arqueo_hideZeroBalance');
        return saved !== null ? JSON.parse(saved) : true; // Default: hide zero balance
    });
    const [hidePastYear, setHidePastYear] = useState(() => {
        const saved = localStorage.getItem('arqueo_hidePastYear');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [onlyPastYear, setOnlyPastYear] = useState(() => {
        const saved = localStorage.getItem('arqueo_onlyPastYear');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // Save filter preferences to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('arqueo_hideTransferred', JSON.stringify(hideTransferred));
    }, [hideTransferred]);

    useEffect(() => {
        localStorage.setItem('arqueo_hideZeroBalance', JSON.stringify(hideZeroBalance));
    }, [hideZeroBalance]);

    useEffect(() => {
        localStorage.setItem('arqueo_hidePastYear', JSON.stringify(hidePastYear));
    }, [hidePastYear]);

    useEffect(() => {
        localStorage.setItem('arqueo_onlyPastYear', JSON.stringify(onlyPastYear));
    }, [onlyPastYear]);

    useEffect(() => {
        localStorage.setItem('arqueo_showFilters', JSON.stringify(showFilters));
    }, [showFilters]);

    const handleViewDetails = async (group) => {
        try {
            setLoading(true);
            const sesionId = sesionActual?.sesion_id || analysis?.sesion_id;
            let movs = [];
            if (sesionId) {
                movs = await cajaService.obtenerMovimientosSesion(sesionId);
            }
            setDetailsModal({
                isOpen: true,
                group,
                movimientos: movs || []
            });
        } catch (err) {
            console.error('Error fetching concept details:', err);
            setDetailsModal({
                isOpen: true,
                group,
                movimientos: []
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalysis();
    }, [modo, sesionActual]);

    const fetchAnalysis = async () => {
        try {
            setLoading(true);

            // 1. Obtener datos de sesión (depósitos, egresos pendientes, info de sesión)
            const data = await cajaService.obtenerAnalisisIngresos(user.id, modo);

            const sessionApertura = sesionActual?.fecha_apertura || data?.fecha_apertura;
            const fechaDesde = sessionApertura 
                ? new Date(sessionApertura).toLocaleDateString('en-CA') 
                : new Date().toLocaleDateString('en-CA');

            // 2. Obtener reporte global para totales CORRECTOS por concepto
            const reporteGlobal = await cajaService.obtenerReporteGlobalConceptos({
                usuario_id: user.id,
                fechaDesde,
                fechaHasta: new Date().toISOString().split('T')[0]
            });

            // 3. Extraer conceptos de Caja General
            const cajaGeneral = reporteGlobal.grupos?.find(c => c.key === 'CAJA_GENERAL');

            // 4. Reemplazar grupos con datos correctos del reporte global
            if (cajaGeneral?.subconceptos?.length > 0) {
                const filteredSubconceptos = cajaGeneral.subconceptos.filter(c => {
                    const key = `${c.concepto}_${c.year}`;
                    return !key.includes('ACADEMIA') && !key.includes('REFINANCIAMIENTO');
                });
                const gruposCorrectos = filteredSubconceptos.map(c => {
                    const key = `${c.concepto}_${c.year}`;
                    const grupoOriginal = data.grupos?.find(g => g.key === key) || {};

                    const isGlobal = modo === 'TODO';
                    const ingresosMonto = isGlobal ? (c.total_ingresos || 0) : (c.desglose_ingresos?.EFECTIVO || 0);
                    const egresosMonto = isGlobal ? (c.total_egresos || 0) : (c.desglose_egresos?.EFECTIVO || 0);

                    // [FIX] Use total_depositos directly from reporteGlobal instead of calculating from data.depositos
                    // This fixes the issue where deposits don't show in MODO LECTURA (closed session)
                    const deposited = c.total_depositos || 0;

                    // [NEW] Extract enviado (habilitaciones enviadas) from global report
                    const enviado = isGlobal ? (c.total_hab_egreso || 0) : (c.desglose_hab_egreso?.EFECTIVO || 0);

                    const pendingCalc = Math.round(Math.max(0, ingresosMonto - deposited - enviado) * 100) / 100;
                    const realPending = Math.max(pendingCalc, c.desglose_ingresos?.EFECTIVO || 0);

                    return {
                        key,
                        label: c.label,
                        total: grupoOriginal.total !== undefined ? grupoOriginal.total : ingresosMonto,
                        egresos: grupoOriginal.egresos !== undefined ? grupoOriginal.egresos : egresosMonto,
                        desglose: c.desglose_ingresos || {},
                        deposited: grupoOriginal.deposited !== undefined ? grupoOriginal.deposited : deposited,
                        enviado, // [NEW] Habilitaciones enviadas
                        detalles_info: grupoOriginal.detalles_info || [],
                        pending: realPending,
                        is_past_year: c.year < new Date().getFullYear(),
                        year: c.year,
                        is_carryover: false
                    };
                });


                const carryoverGrupos = (data.grupos || []).filter(g =>
                    g.is_carryover && g.key !== 'INICIAL_OTROS' && !gruposCorrectos.find(gc => gc.key === g.key)
                );

                data.grupos = [...gruposCorrectos, ...carryoverGrupos]
                    .filter(g => !g.key.includes('ACADEMIA'))
                    .sort((a, b) => b.total - a.total);
                data.total_recaudado = data.grupos.reduce((sum, g) => sum + g.total, 0);

                const totalAsignados = data.grupos.reduce((sum, g) => sum + (g.egresos || 0), 0);
                const totalPendientes = (data.egresos || []).reduce((sum, e) => sum + parseFloat(e.monto), 0);
                data.total_egresos = totalAsignados + totalPendientes;
            }

            setAnalysis(data);

            // Initialize deposit state
            const initialDeposits = {};
            data.grupos.forEach(g => {
                initialDeposits[g.key] = {
                    amount: g.pending,
                    accountId: ''
                };
            });
            setDeposits(initialDeposits);

        } catch (error) {
            console.error('Error loading analysis:', error);
            Swal.fire('Error', 'No se pudo cargar el análisis de caja', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDepositChange = (key, field, value) => {
        setDeposits(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value
            }
        }));
    };

    const executeDeposit = async (group, customData = null) => {
        const depositData = customData || deposits[group.key];

        if (!depositData || !depositData.accountId) {
            return Swal.fire('Atención', 'Selecciona una cuenta de destino', 'warning');
        }
        if (depositData.amount <= 0) {
            return Swal.fire('Atención', 'Monto inválido', 'warning');
        }
        const maxAllowed = Math.max(group.pending || 0, group.total || 0, group.desglose?.EFECTIVO || 0);
        if (depositData.amount > maxAllowed + 0.5) { // Tolerance for float
            return Swal.fire('Atención', 'El monto excede el saldo pendiente', 'warning');
        }

        try {
            setProcessing(true);
            const result = await Swal.fire({
                title: '¿Confirmar Depósito?',
                text: `Se registrará un depósito de S/ ${depositData.amount} por concepto de ${group.label}`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, Depositar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await cajaService.registrarMovimiento({
                    sesion_caja_id: analysis.sesion_id,
                    tipo: 'DEPOSITO_BANCO',
                    monto: depositData.amount,
                    descripcion: `DepósitoCierre: ${group.label}`,
                    cuenta_destino_id: depositData.accountId,
                    usuario_id: user.id
                });

                Swal.fire('Éxito', 'Depósito registrado correctamente', 'success');

                if (depositModal.isOpen) {
                    setDepositModal({ isOpen: false, group: null, amount: '', accountId: '' });
                }

                fetchAnalysis();
            }
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Falló el depósito', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleUndo = async (id, amount, description) => {
        const result = await Swal.fire({
            title: '¿Anular Depósito?',
            html: `Se eliminará el registro de <b>${description}</b> por <b>S/ ${amount}</b>.<br>El dinero regresará al saldo pendiente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, Anular',
            confirmButtonColor: '#d33',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await cajaService.eliminarMovimiento(id);
                Swal.fire('Anulado', 'El depósito ha sido revertido.', 'success');
                fetchAnalysis();
            } catch (error) {
                console.error(error);
                Swal.fire('Error', 'No se pudo anular el depósito', 'error');
                setLoading(false);
            }
        }
    };

    const handleOpenRegularizar = async (totalDisponible) => {
        if (!analysis?.sesion_anterior_id) {
            return Swal.fire('Atención', 'No hay una sesión anterior para regularizar. Esto suele suceder en la primera sesión del sistema.', 'info');
        }

        setRegTargetId(analysis.sesion_anterior_id);
        setRegTotalDisponible(totalDisponible);
        setProcessing(true);

        try {
            // Obtener datos del reporte global para este usuario con filtro amplio
            const reporteGlobal = await cajaService.obtenerReporteGlobalConceptos({
                usuario_id: user.id,
                fechaDesde: '2025-01-01',
                fechaHasta: new Date().toISOString().split('T')[0]
            });

            // Extraer los conceptos de Caja General (que son los que nos interesan)
            console.log('Reporte Global:', reporteGlobal); // Debug
            // El servicio devuelve { grupos: [...], validaciones: {...}, resumen: {...} }
            const grupos = reporteGlobal.grupos || [];
            const cajaGeneral = grupos.find(c => c.key === 'CAJA_GENERAL');

            const conceptosDinamicos = {};

            if (cajaGeneral?.subconceptos) {
                // Usar los subconceptos detallados de Caja General
                cajaGeneral.subconceptos
                    .filter(c => {
                        const key = `${c.concepto}_${c.year}`;
                        return !key.includes('ACADEMIA') && !key.includes('REFINANCIAMIENTO');
                    })
                    .forEach(c => {
                        const key = `${c.concepto}_${c.year}`;
                        const efectivoRecaudado = c.desglose_ingresos?.EFECTIVO || 0;
                        const efectivoEgresado = c.desglose_egresos?.EFECTIVO || 0;
                        conceptosDinamicos[key] = {
                            label: c.label,
                            monto: 0, // Lo que el usuario asignará del saldo anterior
                            recaudado: efectivoRecaudado, // Solo EFECTIVO recaudado
                            total: c.total_ingresos || 0, // Total con todos los métodos (referencia)
                            efectivo: efectivoRecaudado,
                            yape: c.desglose_ingresos?.YAPE || 0,
                            plin: c.desglose_ingresos?.PLIN || 0,
                            transferencia: c.desglose_ingresos?.TRANSFERENCIA || 0,
                            egresosAsignados: efectivoEgresado, // Egresos en efectivo
                            saldoNeto: efectivoRecaudado - efectivoEgresado, // Saldo disponible en efectivo
                            pagos: c.cantidad_pagos || 0
                        };
                    });
            }

            // Agregar "OTROS" por si hay saldos sin categoría específica
            const year = new Date().getFullYear();
            if (!conceptosDinamicos[`OTROS_${year}`]) {
                conceptosDinamicos[`OTROS_${year}`] = {
                    label: `OTROS INGRESOS ${year}`,
                    monto: 0, recaudado: 0, egresosAsignados: 0, saldoNeto: 0, efectivo: 0
                };
            }

            setRegBreakdown(conceptosDinamicos);

            // Initialize expense assignments from existing tags if any
            // Include BOTH unassigned (egresos) and assigned (egresosAsignados) for editing
            const initialExp = {};
            const todosLosEgresos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];
            todosLosEgresos.forEach(e => {
                const match = (e.observaciones || '').match(/\[ORIGEN:([^\]]+)\]/);
                initialExp[e.id] = match ? match[1] : '';
            });
            setRegExpenses(initialExp);

            setIsRegModalOpen(true);
        } catch (error) {
            console.error('Error cargando datos de regularización:', error);
            Swal.fire('Error', 'No se pudieron cargar los datos de conceptos', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleRegExpenseChange = (id, conceptKey) => {
        setRegExpenses(prev => ({ ...prev, [id]: conceptKey }));
    };

    // Calcular dinámicamente los egresos asignados a cada concepto
    const egresosCalculados = useMemo(() => {
        const todosEgresos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];
        const calculado = {};

        // Inicializar en 0 para todos los conceptos
        Object.keys(regBreakdown).forEach(key => {
            calculado[key] = 0;
        });

        // Sumar egresos por concepto asignado
        todosEgresos.forEach(egr => {
            const conceptoAsignado = regExpenses[egr.id];
            if (conceptoAsignado && calculado[conceptoAsignado] !== undefined) {
                calculado[conceptoAsignado] += parseFloat(egr.monto) || 0;
            }
        });

        return calculado;
    }, [regExpenses, regBreakdown, analysis]);

    // Calcular saldo neto dinámico (recaudado - egresos asignados en tiempo real)
    const saldoNetoDinamico = useMemo(() => {
        const saldos = {};
        Object.entries(regBreakdown).forEach(([key, data]) => {
            const egresosDelConcepto = egresosCalculados[key] || 0;
            saldos[key] = (data.recaudado || 0) - egresosDelConcepto;
        });
        return saldos;
    }, [regBreakdown, egresosCalculados]);

    // Calcular el máximo distribuible (suma de saldos netos, no puede ser mayor que el saldo anterior)
    const totalMaximoDistribuible = useMemo(() => {
        const sumaNetosPositivos = Object.values(saldoNetoDinamico).reduce((sum, val) => sum + Math.max(0, val), 0);
        return Math.min(sumaNetosPositivos, regTotalDisponible);
    }, [saldoNetoDinamico, regTotalDisponible]);

    const handleRegValueChange = (key, value) => {
        setRegBreakdown(prev => ({
            ...prev,
            [key]: { ...prev[key], monto: parseFloat(value) || 0 }
        }));
    };

    const saveRegularizacion = async () => {
        const totalAsignado = Object.values(regBreakdown).reduce((sum, item) => sum + item.monto, 0);

        if (totalAsignado > regTotalDisponible + 0.01) {
            return Swal.fire('Error', 'El total asignado supera el monto disponible para regularizar.', 'error');
        }

        try {
            setProcessing(true);
            // 1. Actualizar desglose de sesión anterior
            await cajaService.actualizarDesgloseSesion(regTargetId, regBreakdown);

            // 2. Actualizar conceptos de egresos de la sesión actual
            const asignaciones = Object.entries(regExpenses).map(([id, conceptKey]) => ({
                id: parseInt(id),
                conceptoKey: conceptKey
            }));
            await cajaService.asignarConceptosEgresos(asignaciones);

            Swal.fire('Éxito', 'Regularización general completada correctamente', 'success');
            setIsRegModalOpen(false);
            fetchAnalysis(); // Recargar datos
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudo guardar la regularización', 'error');
        } finally {
            setProcessing(false);
        }
    };

    // Función para guardar asignación directa de gastos desde la vista principal
    const handleDirectExpenseChange = (egresoId, conceptKey) => {
        setDirectExpenseAssign(prev => ({ ...prev, [egresoId]: conceptKey }));
    };

    const saveDirectExpenseAssignments = async () => {
        const asignaciones = Object.entries(directExpenseAssign)
            .filter(([id, key]) => key && key !== '') // Solo las que tienen asignación
            .map(([id, conceptoKey]) => ({
                id: parseInt(id),
                conceptoKey
            }));

        if (asignaciones.length === 0) {
            return Swal.fire('Atención', 'No hay gastos para asignar', 'info');
        }

        try {
            setProcessing(true);
            await cajaService.asignarConceptosEgresos(asignaciones);
            Swal.fire('Éxito', `${asignaciones.length} gasto(s) asignado(s) correctamente`, 'success');
            fetchAnalysis(); // Recargar datos
            setDirectExpenseAssign({}); // Limpiar estado
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudieron asignar los gastos', 'error');
        } finally {
            setProcessing(false);
        }
    };

    // Apply filters to grupos (MUST be before early return to follow Rules of Hooks)
    const filteredGrupos = useMemo(() => {
        if (!analysis?.grupos) return [];

        return analysis.grupos.filter(group => {
            // Filter 1: Hide transferred concepts (TRANSF)
            if (hideTransferred && group.label.includes('(TRANSF)')) {
                return false;
            }

            // Filter 2: Hide zero balance concepts (Solo ocultar si está en cero absoluto sin movimientos)
            if (hideZeroBalance && Math.abs(group.pending) < 0.001 && group.total === 0 && (group.egresos || 0) === 0) {
                return false;
            }

            // Filter 3: Hide past year debt
            if (hidePastYear && group.is_past_year) {
                return false;
            }

            // Filter 4: Only past year debt
            if (onlyPastYear && !group.is_past_year) {
                return false;
            }

            return true;
        });
    }, [analysis?.grupos, hideTransferred, hideZeroBalance, hidePastYear, onlyPastYear]);

    const hiddenCount = (analysis?.grupos?.length || 0) - filteredGrupos.length;
    const totalIngresos = analysis?.total_recaudado || 0;
    const totalDepositar = Object.values(deposits).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    const saldoFinal = totalIngresos - totalDepositar;

    if (loading) return <div className="loading-spinner">Analizando Ingresos...</div>;

    return (
        <div className="arqueo-distribucion">
            <div className="ad-header">
                <div className="d-flex align-items-center gap-3">
                    <h2><i className="flaticon-381-networking"></i> Arqueo y Distribución</h2>
                    <button
                        className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <i className="fa fa-sliders-h mr-2"></i> Filtros
                    </button>
                </div>

                <div className="d-flex align-items-center gap-2">
                    {!analysis?.sesion_id && (
                        <span className="badge badge-warning py-2 px-3" style={{ fontSize: '0.9rem' }}>
                            <i className="fas fa-lock me-2"></i> MODO LECTURA (CAJA CERRADA)
                        </span>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={onClose}>
                        <i className="flaticon-381-multiply-1"></i> Cerrar
                    </button>
                </div>
            </div>

            {/* Active Filter Chips - NOW OUTSIDE HEADER */}
            <div className="active-filters-chips d-flex align-items-center flex-wrap gap-2 mb-3">
                <div className={`badge badge-glass ${modo === 'EFECTIVO' ? 'badge-glass-success' : 'badge-glass-info'} text-white rounded-pill`}>
                    <i className={`fa ${modo === 'EFECTIVO' ? 'fa-money-bill-wave' : 'fa-globe'} mr-2`}></i>
                    {modo === 'EFECTIVO' ? 'Solo Efectivo' : 'Global (Bancos + Efect.)'}
                </div>
                {hideTransferred && (
                    <div className="badge badge-glass badge-glass-secondary text-white rounded-pill d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setHideTransferred(false)}>
                        <span className="mr-1">Sin TRANSF</span>
                        <i className="fa fa-times ml-1"></i>
                    </div>
                )}
                {hideZeroBalance && (
                    <div className="badge badge-glass badge-glass-secondary text-white rounded-pill d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setHideZeroBalance(false)}>
                        <span className="mr-1">Sin S/ 0</span>
                        <i className="fa fa-times ml-1"></i>
                    </div>
                )}
                {hidePastYear && (
                    <div className="badge badge-glass badge-glass-warning text-white rounded-pill d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setHidePastYear(false)}>
                        <span className="mr-1">Sin Deuda {new Date().getFullYear() - 1}</span>
                        <i className="fa fa-times ml-1"></i>
                    </div>
                )}
                {onlyPastYear && (
                    <div className="badge badge-glass badge-glass-warning text-white rounded-pill d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOnlyPastYear(false)}>
                        <span className="mr-1">Solo Deuda Pasada</span>
                        <i className="fa fa-times ml-1"></i>
                    </div>
                )}
                {hiddenCount > 0 && (
                    <span className="badge badge-info-outline rounded-pill" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                        <i className="fa fa-eye-slash mr-1"></i>
                        {hiddenCount} oculto{hiddenCount !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Collapsible Filters Panel - NOW OUTSIDE HEADER */}
            {showFilters && (
                <div className="ad-filter-panel animate__animated animate__fadeInDown mb-4">
                    <div className="row align-items-end">
                        <div className="col-md-4">
                            <label className="filter-label">Modo de Vista</label>
                            <div className="btn-group w-100">
                                <button
                                    className={`btn btn-sm ${modo === 'EFECTIVO' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setModo('EFECTIVO')}
                                >SOLO EFECTIVO</button>
                                <button
                                    className={`btn btn-sm ${modo === 'TODO' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setModo('TODO')}
                                >GLOBAL (BANCOS + EFECT.)</button>
                            </div>
                        </div>
                        <div className="col-md-5">
                            <label className="filter-label">Visibilidad de Conceptos</label>
                            <div className="d-flex gap-2">
                                <button
                                    className={`btn btn-sm flex-grow-1 ${hideTransferred ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => setHideTransferred(!hideTransferred)}
                                >
                                    <i className={`fa ${hideTransferred ? 'fa-check' : 'fa-exchange-alt'} mr-2`}></i>
                                    Ocultar TRANSF
                                </button>
                                <button
                                    className={`btn btn-sm flex-grow-1 ${hideZeroBalance ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => setHideZeroBalance(!hideZeroBalance)}
                                >
                                    <i className={`fa ${hideZeroBalance ? 'fa-check' : 'fa-filter'} mr-2`}></i>
                                    Ocultar S/ 0
                                </button>
                                <button
                                    className={`btn btn-sm flex-grow-1 ${hidePastYear ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => {
                                        setHidePastYear(!hidePastYear);
                                        if (!hidePastYear) setOnlyPastYear(false);
                                    }}
                                >
                                    <i className={`fa ${hidePastYear ? 'fa-check' : 'fa-history'} mr-2`}></i>
                                    Ocultar Deuda
                                </button>
                                <button
                                    className={`btn btn-sm flex-grow-1 ${onlyPastYear ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => {
                                        setOnlyPastYear(!onlyPastYear);
                                        if (!onlyPastYear) setHidePastYear(false);
                                    }}
                                >
                                    <i className={`fa ${onlyPastYear ? 'fa-check' : 'fa-history'} mr-2`}></i>
                                    Solo Deuda
                                </button>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="filter-label">Tipo de Vista</label>
                            <div className="btn-group w-100">
                                <button
                                    className={`btn btn-sm ${viewType === 'GRID' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewType('GRID')}
                                    title="Vista de Tarjetas"
                                >
                                    <i className="fa fa-th-large mr-1"></i> Tarjetas
                                </button>
                                <button
                                    className={`btn btn-sm ${viewType === 'TABLE' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewType('TABLE')}
                                    title="Vista de Tabla"
                                >
                                    <i className="fa fa-list mr-1"></i> Tabla
                                </button>
                            </div>
                        </div>
                        <div className="col-md-2 text-right">
                            <button
                                className="btn btn-link btn-sm text-danger text-decoration-none"
                                onClick={() => {
                                    setModo('EFECTIVO');
                                    setHideTransferred(false);
                                    setHideZeroBalance(true);
                                    setHidePastYear(false);
                                    setOnlyPastYear(false);
                                    setViewType('GRID');
                                }}
                            >
                                <i className="fa fa-eraser mr-1"></i> Limpiar
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* [NEW] Tarjeta de Desglose de Efectivo Físico */}
            {
                (() => {
                    const montoInicial = parseFloat(sesionActual?.monto_inicial || 0);
                    const totalEfectivo = analysis.total_recaudado;
                    
                    // Sumar todos los gastos (pendientes + asignados)
                    const todosGastos = [...(analysis.egresos || []), ...(analysis.egresosAsignados || [])];
                    const totalGastos = todosGastos.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
                    
                    // Depósitos realizados EXCLUSIVAMENTE en esta sesión activa
                    const depositosSesionActual = (analysis.depositos || [])
                        .filter(d => d.sesion_caja_id === sesionActual?.sesion_id)
                        .reduce((sum, d) => sum + parseFloat(d.monto || 0), 0);

                    const totalEnviado = analysis.grupos.reduce((sum, g) => sum + (g.enviado || 0), 0);

                    // Saldo Físico Esperado = Inicial + Cobros Efectivo - Gastos - Depósitos de la Sesión - Enviados
                    const efectivoFisico = Math.round((montoInicial + totalEfectivo - totalGastos - depositosSesionActual - totalEnviado) * 100) / 100;

                    return (
                        <div className="mt-4 mb-4 p-4" style={{
                            background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(16,185,129,0.1))',
                            borderRadius: '12px',
                            border: '1px solid rgba(59,130,246,0.3)'
                        }}>
                            <h5 className="mb-3" style={{ color: '#3b82f6' }}>
                                <i className="fa fa-wallet mr-2"></i>
                                Efectivo Físico en Caja (Billetes y Monedas en Mano)
                            </h5>
                            <div className="row align-items-center">
                                {montoInicial > 0 && (
                                    <div className="col-md-2">
                                        <div className="text-muted small">Saldo Inicial</div>
                                        <div className="h4 mb-0 text-primary">+ S/ {montoInicial.toFixed(2)}</div>
                                    </div>
                                )}
                                <div className="col-md-2">
                                    <div className="text-muted small">Total Recaudado (Efectivo)</div>
                                    <div className="h4 mb-0" style={{ color: '#10b981' }}>+ S/ {totalEfectivo.toFixed(2)}</div>
                                </div>
                                <div className="col-md-2">
                                    <div className="text-muted small">Gastos Pagados</div>
                                    <div className="h4 mb-0" style={{ color: '#ef4444' }}>- S/ {totalGastos.toFixed(2)}</div>
                                </div>
                                {depositosSesionActual > 0 && (
                                    <div className="col-md-2">
                                        <div className="text-muted small">Depósitos (Esta Sesión)</div>
                                        <div className="h4 mb-0" style={{ color: '#f59e0b' }}>- S/ {depositosSesionActual.toFixed(2)}</div>
                                    </div>
                                )}
                                {totalEnviado > 0 && (
                                    <div className="col-md-2">
                                        <div className="text-muted small">Enviado a Otras Cajas</div>
                                        <div className="h4 mb-0" style={{ color: '#f97316' }}>- S/ {totalEnviado.toFixed(2)}</div>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <div className="text-muted small fw-bold text-uppercase">Debe haber en caja (Físico Real)</div>
                                    <div className="h3 mb-0 font-weight-bold" style={{
                                        color: efectivoFisico >= 0 ? '#10b981' : '#ef4444',
                                        fontSize: '2.2rem'
                                    }}>
                                        = S/ {efectivoFisico.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2 pt-2 border-top" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                <small className="text-muted">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    Este es el monto exacto en billetes y monedas que físicamente debes tener en tu sobre/cajón ahora mismo.
                                </small>
                            </div>
                        </div>
                    );
                })()
            }

            {/* Resumen de Gastos y Botón para Modal */}
            {
                (analysis?.egresos?.length > 0 || analysis?.egresosAsignados?.length > 0) && (() => {
                    const todosGastos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];
                    const pendientes = todosGastos.filter(g => !(g.observaciones || '').includes('[ORIGEN:'));
                    const asignados = todosGastos.filter(g => (g.observaciones || '').includes('[ORIGEN:'));

                    return (
                        <div className="gastos-resumen-section mt-4 mb-4 p-3" style={{
                            background: pendientes.length > 0
                                ? 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(245,158,11,0.1))'
                                : 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(59,130,246,0.1))',
                            borderRadius: '12px',
                            border: pendientes.length > 0 ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(16,185,129,0.3)'
                        }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="mb-1" style={{ color: pendientes.length > 0 ? '#ef4444' : '#10b981' }}>
                                        <i className={`fa ${pendientes.length > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'} mr-2`}></i>
                                        Gestión de Gastos
                                    </h5>
                                    <div className="d-flex gap-3">
                                        <span className="badge" style={{ background: '#ef4444', color: 'white', padding: '5px 10px' }}>
                                            {pendientes.length} Pendientes
                                        </span>
                                        <span className="badge" style={{ background: '#10b981', color: 'white', padding: '5px 10px' }}>
                                            {asignados.length} Asignados
                                        </span>
                                        <span className="badge" style={{ background: '#6b7280', color: 'white', padding: '5px 10px' }}>
                                            Total: S/ {todosGastos.reduce((s, g) => s + parseFloat(g.monto), 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setIsGastosModalOpen(true)}
                                    title={!analysis?.sesion_id ? "Modo Lectura: Ver lista de gastos" : "Gestionar y asignar gastos"}
                                >
                                    <i className="fa fa-list-alt mr-2"></i>
                                    {analysis?.sesion_id ? 'Gestionar Gastos' : 'Ver Lista de Gastos'}
                                </button>
                            </div>
                        </div>
                    );
                })()
            }

            {viewType === 'GRID' ? (
                <div className="ad-grid">
                    {filteredGrupos.map(group => (
                        <div key={group.key} className={`concept-card ${group.is_past_year ? 'past-year' : ''}`}>
                            <div className="concept-header">
                                <span className="concept-icon">
                                    <i className={getIconForConcept(group.label)}></i>
                                </span>
                                <div className="concept-info">
                                    <h3>{group.label}</h3>
                                    <div className="concept-meta-detailed">
                                        {group.is_past_year && <span className="badge badge-warning">Deuda {group.year}</span>}
                                        {group.is_carryover && <span className="badge badge-info" style={{ backgroundColor: '#e3f2fd', color: '#1976d2', border: '1px solid #bbdefb' }}>Saldo Anterior</span>}
                                        {group.key === 'INICIAL_OTROS' && analysis?.sesion_anterior_id && (
                                            <button
                                                className="btn-reg ml-2"
                                                onClick={() => handleOpenRegularizar(group.total)}
                                                disabled={!analysis?.sesion_id}
                                            >
                                                <i className="fa fa-magic mr-1"></i> Regularizar
                                            </button>
                                        )}
                                        <div className="meta-row text-success">
                                            <span>Recaudado:</span> <strong>S/ {group.total.toFixed(2)}</strong>
                                        </div>

                                        {/* Desglose visual en modo Global */}
                                        {modo === 'TODO' && group.desglose && (
                                            <div className="concept-desglose-pills mb-2">
                                                {group.desglose.YAPE > 0 && (
                                                    <span className="badge-metodo yape" title="Yape">
                                                        <i className="fa fa-phone-alt"></i> S/ {group.desglose.YAPE.toFixed(2)}
                                                    </span>
                                                )}
                                                {group.desglose.PLIN > 0 && (
                                                    <span className="badge-metodo plin" title="Plin">
                                                        <i className="fa fa-qrcode"></i> S/ {group.desglose.PLIN.toFixed(2)}
                                                    </span>
                                                )}
                                                {group.desglose.TRANSFERENCIA > 0 && (
                                                    <span className="badge-metodo transfer" title="Transferencia">
                                                        <i className="fa fa-university"></i> S/ {group.desglose.TRANSFERENCIA.toFixed(2)}
                                                    </span>
                                                )}
                                                {group.desglose.EFECTIVO > 0 && (
                                                    <span className="badge-metodo cash" title="Efectivo">
                                                        <i className="fa fa-money-bill-wave"></i> S/ {group.desglose.EFECTIVO.toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <div className="meta-row text-danger">
                                            <span>Egresos:</span> <strong>- S/ {(group.egresos || 0).toFixed(2)}</strong>
                                        </div>
                                        {/* [NEW] Mostrar Enviado (Habilitaciones Enviadas) */}
                                        {(group.enviado || 0) > 0 && (
                                            <div className="meta-row text-warning">
                                                <span>Enviado:</span> <strong>- S/ {(group.enviado || 0).toFixed(2)}</strong>
                                            </div>
                                        )}
                                        <div className="meta-row text-info">
                                            <span>Depositado:</span> <strong>S/ {group.deposited.toFixed(2)}</strong>
                                        </div>
                                        <div className="meta-row text-primary pt-1 border-top mt-1" style={{ fontSize: '1.1rem' }}>
                                            <span>Disponible:</span> <strong>S/ {group.pending.toFixed(2)}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="concept-actions">
                                <div className="form-group">
                                    <label>Monto a Depositar</label>
                                    <div className="input-group">
                                        <span className="input-group-text">S/</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={deposits[group.key]?.amount || ''}
                                            onChange={(e) => handleDepositChange(group.key, 'amount', e.target.value)}
                                            max={group.pending}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Cuenta Destino</label>
                                    <select
                                        className="form-control"
                                        value={deposits[group.key]?.accountId || ''}
                                        onChange={(e) => handleDepositChange(group.key, 'accountId', e.target.value)}
                                    >
                                        <option value="">Seleccionar Banco...</option>
                                        {analysis.cuentas.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.banco} - {c.moneda} - {c.numero_cuenta}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    className="btn btn-success btn-block"
                                    disabled={processing || !deposits[group.key]?.accountId || group.pending <= 0 || !analysis?.sesion_id}
                                    onClick={() => executeDeposit(group)}
                                >
                                    <i className="flaticon-381-price-tag"></i> {analysis?.sesion_id ? 'Realizar Depósito' : 'Abrir Caja para Depositar'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="ad-table-container animate__animated animate__fadeIn">
                    <table className="ad-table">
                        <thead>
                            <tr>
                                <th>Concepto</th>
                                <th className="text-right">Saldo Anterior</th>
                                <th className="text-right">Recaudado (Hoy)</th>
                                <th className="text-right">Egresos</th>
                                <th className="text-right">Depósitos</th>
                                <th className="text-right">Disponible Final</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGrupos.map(group => (
                                <tr key={group.key} className={group.is_past_year ? 'row-past-year' : ''}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <i className={getIconForConcept(group.label)} style={{ color: 'var(--primary)', width: '20px' }}></i>
                                            <div className="d-flex flex-column">
                                                <span className="font-weight-600">{group.label}</span>
                                                {group.is_past_year && <small className="text-warning">Deuda {group.year}</small>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-right col-carryover font-weight-bold text-info">
                                        S/ {(group.carryover || 0).toFixed(2)}
                                    </td>
                                    <td className={`text-right col-recaudado ${group.total > 0 ? 'text-success font-weight-bold' : ''}`}>
                                        {group.total > 0 ? `+ S/ ${group.total.toFixed(2)}` : 'S/ 0.00'}
                                    </td>
                                    <td className={`text-right col-egresos ${group.egresos > 0 ? 'text-danger font-weight-bold' : 'text-muted'}`}>
                                        {group.egresos > 0 ? `- S/ ${group.egresos.toFixed(2)}` : 'S/ 0.00'}
                                    </td>
                                    <td className={`text-right col-depositos ${group.deposited > 0 ? 'text-primary font-weight-bold' : 'text-muted'}`}>
                                        {group.deposited > 0 ? `- S/ ${group.deposited.toFixed(2)}` : 'S/ 0.00'}
                                    </td>
                                    <td className="text-right col-disponible font-weight-bold" style={{ color: group.pending > 0 ? '#10b981' : '#94a3b8' }}>
                                        S/ {group.pending.toFixed(2)}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1 justify-content-center">
                                            <button
                                                className="btn btn-sm btn-success"
                                                onClick={() => setDepositModal({
                                                    isOpen: true,
                                                    group: group,
                                                    amount: group.pending.toFixed(2),
                                                    accountId: ''
                                                })}
                                                disabled={!analysis?.sesion_id || group.pending <= 0}
                                                title={!analysis?.sesion_id ? "Caja cerrada" : "Realizar Depósito"}
                                            >
                                                <i className="fa fa-university mr-1"></i> Depositar
                                            </button>

                                            <button
                                                className="btn btn-outline-info btn-sm"
                                                onClick={() => handleViewDetails(group)}
                                                title="Ver detalle"
                                            >
                                                <i className="fa fa-info-circle"></i>
                                            </button>

                                            {group.key === 'INICIAL_OTROS' && analysis?.sesion_anterior_id && (
                                                <button
                                                    className="btn btn-outline-warning btn-sm"
                                                    onClick={() => handleOpenRegularizar(group.total)}
                                                    disabled={!analysis?.sesion_id}
                                                    title="Regularizar"
                                                >
                                                    <i className="fa fa-magic"></i>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="ad-table-footer">
                            <tr>
                                <td className="font-weight-bold">TOTAL GENERAL</td>
                                <td className="text-right font-weight-bold text-info">
                                    S/ {filteredGrupos.reduce((sum, g) => sum + (g.carryover || 0), 0).toFixed(2)}
                                </td>
                                <td className="text-right font-weight-bold text-success">
                                    + S/ {filteredGrupos.reduce((sum, g) => sum + g.total, 0).toFixed(2)}
                                </td>
                                <td className="text-right font-weight-bold col-egresos text-danger">
                                    {filteredGrupos.reduce((sum, g) => sum + (g.egresos || 0), 0) > 0 
                                        ? `- S/ ${filteredGrupos.reduce((sum, g) => sum + (g.egresos || 0), 0).toFixed(2)}` 
                                        : 'S/ 0.00'}
                                </td>
                                <td className="text-right font-weight-bold col-depositos text-primary">
                                    {filteredGrupos.reduce((sum, g) => sum + (g.deposited || 0), 0) > 0 
                                        ? `- S/ ${filteredGrupos.reduce((sum, g) => sum + (g.deposited || 0), 0).toFixed(2)}` 
                                        : 'S/ 0.00'}
                                </td>
                                <td className="text-right font-weight-bold col-disponible text-success" style={{ fontSize: '1.1rem' }}>
                                    S/ {filteredGrupos.reduce((sum, g) => sum + g.pending, 0).toFixed(2)}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Deposit History Section */}
            {
                analysis.depositos && analysis.depositos.length > 0 && (
                    <div className="ad-history-section">
                        <h3><i className="flaticon-381-clock"></i> Historial de Depósitos (Sesión Actual)</h3>
                        <div className="table-responsive">
                            <table className="table table-sm table-striped">
                                <thead>
                                    <tr>
                                        <th>Hora</th>
                                        <th>Descripción</th>
                                        <th>Monto</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysis.depositos.map(dep => (
                                        <tr key={dep.id}>
                                            <td>{new Date(dep.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td>{dep.descripcion} <small className="text-muted">(ID: {dep.id})</small></td>
                                            <td className="text-success font-weight-bold">S/ {parseFloat(dep.monto).toFixed(2)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-danger btn-xs"
                                                    onClick={() => handleUndo(dep.id, dep.monto, dep.descripcion)}
                                                    title="Revertir Depósito"
                                                    disabled={!analysis?.sesion_id}
                                                >
                                                    <i className="flaticon-381-trash-1"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            }

            {/* Modal de Regularización Avanzada */}
            {
                isRegModalOpen && (
                    <div className="reg-modal-overlay">
                        <div className="reg-modal">
                            <div className="reg-modal-header">
                                <h3><i className="fa fa-balance-scale mr-2"></i> Mesa de Trabajo: Regularización de Sesión</h3>
                                <button className="close-btn border-0 bg-transparent text-white" onClick={() => setIsRegModalOpen(false)}>×</button>
                            </div>

                            <div className="reg-modal-body">
                                {/* COLUMNA 1: DISTRIBUCIÓN DE SALDO ANTERIOR */}
                                <div className="reg-modal-col">
                                    <h4 className="reg-col-title">
                                        <i className="fa fa-history"></i> Saldo Anterior (S/ {regTotalDisponible.toFixed(2)})
                                    </h4>
                                    <p className="text-muted small mb-4">
                                        Distribuye el saldo que venía de la sesión anterior en los conceptos correctos.
                                    </p>

                                    <div className="reg-breakdown-list pr-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {Object.entries(regBreakdown).map(([key, data]) => {
                                            const netoDinamico = saldoNetoDinamico[key] || 0;
                                            const egresosAsignados = egresosCalculados[key] || 0;
                                            return (
                                                <div key={key} className="reg-row" style={{ flexDirection: 'column', alignItems: 'stretch', marginBottom: '8px' }}>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div style={{ flex: 1 }}>
                                                            <label className="mb-0 d-block" style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{data.label}</label>
                                                            <small style={{ color: '#6b7280', fontSize: '0.65rem' }}>
                                                                💵 Efectivo: S/ {(data.recaudado || 0).toFixed(2)}
                                                                {egresosAsignados > 0 && (
                                                                    <span style={{ color: '#ef4444' }}> - S/ {egresosAsignados.toFixed(2)}</span>
                                                                )}
                                                                {' = '}
                                                                <span style={{ color: netoDinamico > 0 ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                                                                    S/ {netoDinamico.toFixed(2)}
                                                                </span>
                                                            </small>
                                                        </div>
                                                        <div className="input-group" style={{ maxWidth: '100px' }}>
                                                            <span className="input-group-text" style={{ padding: '2px 6px', fontSize: '0.75rem' }}>S/</span>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-right"
                                                                value={data.monto || ''}
                                                                onChange={(e) => handleRegValueChange(key, e.target.value)}
                                                                placeholder="0"
                                                                max={Math.max(0, netoDinamico)}
                                                                style={{ padding: '2px 6px', fontSize: '0.8rem' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Botón para auto-rellenar con valores sugeridos */}
                                    <button
                                        className="btn btn-outline-info btn-sm w-100 mb-3"
                                        onClick={() => {
                                            setRegBreakdown(prev => {
                                                const nuevo = { ...prev };
                                                Object.entries(nuevo).forEach(([key, data]) => {
                                                    const neto = saldoNetoDinamico[key] || 0;
                                                    nuevo[key] = { ...data, monto: Math.max(0, neto) };
                                                });
                                                return nuevo;
                                            });
                                        }}
                                    >
                                        <i className="fa fa-magic mr-1"></i> Auto-rellenar con saldos sugeridos
                                    </button>

                                    <div className="mt-auto pt-3 border-top">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="font-weight-bold">Total Asignado:</span>
                                            <span className={`reg-total ${Object.values(regBreakdown).reduce((s, i) => s + i.monto, 0) > regTotalDisponible + 0.01 ? 'text-danger' : 'text-success'}`}>
                                                S/ {Object.values(regBreakdown).reduce((s, i) => s + i.monto, 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between small text-muted mb-1">
                                            <span>Máximo distribuible:</span>
                                            <span>S/ {totalMaximoDistribuible.toFixed(2)}</span>
                                        </div>
                                        <div className={`text-right small ${Object.values(regBreakdown).reduce((s, i) => s + i.monto, 0) > regTotalDisponible + 0.01 ? 'text-danger' : 'text-muted'}`}>
                                            Pendiente: S/ {(regTotalDisponible - Object.values(regBreakdown).reduce((s, i) => s + i.monto, 0)).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                {/* COLUMNA 2: ASIGNACIÓN DE GASTOS */}
                                <div className="reg-modal-col border-left pl-4">
                                    <h4 className="reg-col-title text-danger">
                                        <i className="fa fa-receipt"></i> Asignación de Gastos (Egresos)
                                    </h4>
                                    <p className="text-muted small mb-3">
                                        Asigna cada gasto a un concepto para que se descuente del saldo correspondiente.
                                    </p>

                                    <div className="reg-expenses-list pr-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        {(() => {
                                            // Combinar todos los egresos (sin asignar + asignados)
                                            const todosEgresos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];

                                            if (todosEgresos.length === 0) {
                                                return (
                                                    <div className="text-center py-4 text-muted">
                                                        <i className="fa fa-info-circle fa-2x mb-2"></i>
                                                        <p className="mb-0">No hay gastos en las últimas sesiones.</p>
                                                    </div>
                                                );
                                            }

                                            return todosEgresos.map(egr => {
                                                const estaAsignado = regExpenses[egr.id] && regExpenses[egr.id] !== '';
                                                return (
                                                    <div
                                                        key={egr.id}
                                                        className="reg-expense-item mb-2 p-2 border rounded"
                                                        style={{
                                                            background: estaAsignado ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.03)',
                                                            borderColor: estaAsignado ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'
                                                        }}
                                                    >
                                                        <div className="d-flex justify-content-between small font-weight-bold mb-1">
                                                            <span style={{ color: '#e2e8f0' }}>{egr.concepto}</span>
                                                            <span className="text-danger">S/ {parseFloat(egr.monto).toFixed(2)}</span>
                                                        </div>
                                                        <select
                                                            className="form-control form-control-sm"
                                                            style={{ fontSize: '0.8rem', background: '#2d2d44', color: '#e2e8f0', border: '1px solid #3d3d5c' }}
                                                            value={regExpenses[egr.id] || ''}
                                                            onChange={(e) => handleRegExpenseChange(egr.id, e.target.value)}
                                                        >
                                                            <option value="">-- Sin asignar (Gasto General) --</option>
                                                            {analysis.grupos.map(g => (
                                                                <option key={g.key} value={g.key}>
                                                                    {g.label} (Neto: S/ {((g.total || 0) - (g.egresos || 0)).toFixed(2)})
                                                                </option>
                                                            ))}
                                                            {Object.entries(regBreakdown).map(([k, v]) => (
                                                                <option key={k} value={k}>{v.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div className="reg-modal-footer p-4 border-top">
                                <div className="d-flex gap-2 w-100">
                                    <button className="btn btn-outline-secondary" style={{ flex: 1 }} onClick={() => setIsRegModalOpen(false)}>Cancelar</button>
                                    <button
                                        className="btn btn-primary"
                                        style={{ flex: 2 }}
                                        onClick={saveRegularizacion}
                                        disabled={processing || Object.values(regBreakdown).reduce((s, i) => s + i.monto, 0) > regTotalDisponible + 0.01}
                                    >
                                        {processing ? (
                                            <><i className="fa fa-spinner fa-spin mr-2"></i> Procesando...</>
                                        ) : (
                                            <><i className="fa fa-save mr-2"></i> Confirmar Regularización Total</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal Dedicado para Gestión de Gastos */}
            {
                isGastosModalOpen && (
                    <div
                        className="reg-overlay"
                        onClick={() => setIsGastosModalOpen(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            background: 'rgba(0,0,0,0.5)',
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <div
                            className="reg-modal"
                            onClick={e => e.stopPropagation()}
                            style={{
                                maxWidth: '900px',
                                background: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                                overflow: 'hidden',
                                maxHeight: '90vh',
                                display: 'flex',
                                flexDirection: 'column',
                                width: '90%'
                            }}
                        >
                            <div className="reg-modal-header" style={{ background: 'linear-gradient(135deg, #ef4444, #f59e0b)', padding: '20px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0, fontSize: '1.25rem' }}><i className="fa fa-receipt mr-2"></i> Gestión de Gastos - Asignar a Conceptos</h3>
                                <button className="close-btn border-0 bg-transparent text-white" style={{ fontSize: '1.5rem', cursor: 'pointer' }} onClick={() => setIsGastosModalOpen(false)}>×</button>
                            </div>

                            <div className="reg-modal-body" style={{ display: 'block', padding: '20px' }}>
                                {/* Barra de búsqueda y estadísticas */}
                                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                                    <div className="input-group" style={{ maxWidth: '350px' }}>
                                        <span className="input-group-text"><i className="fa fa-search"></i></span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar gasto por concepto, proveedor..."
                                            value={searchGastos}
                                            onChange={(e) => setSearchGastos(e.target.value)}
                                        />
                                        {searchGastos && (
                                            <button className="btn btn-outline-secondary" onClick={() => setSearchGastos('')}>
                                                <i className="fa fa-times"></i>
                                            </button>
                                        )}
                                    </div>

                                    {(() => {
                                        const todosGastos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];
                                        const pendientes = todosGastos.filter(g => {
                                            const searchStr = `${g.concepto || ''} ${g.observaciones || ''}`;
                                            const asignadoEnModal = directExpenseAssign[g.id];
                                            return !searchStr.includes('[ORIGEN:') && !asignadoEnModal;
                                        });
                                        const asignados = todosGastos.filter(g => {
                                            const searchStr = `${g.concepto || ''} ${g.observaciones || ''}`;
                                            const asignadoEnModal = directExpenseAssign[g.id];
                                            return searchStr.includes('[ORIGEN:') || asignadoEnModal;
                                        });
                                        return (
                                            <div className="d-flex gap-2">
                                                <span className="badge bg-danger" style={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                                                    <i className="fa fa-clock mr-1"></i> {pendientes.length} Pendientes
                                                </span>
                                                <span className="badge bg-success" style={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                                                    <i className="fa fa-check-circle mr-1"></i> {asignados.length} Asignados
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Lista de Gastos */}
                                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                                    {(() => {
                                        const todosGastos = [...(analysis?.egresos || []), ...(analysis?.egresosAsignados || [])];
                                        const gastosFiltrados = todosGastos.filter(g => {
                                            if (!searchGastos) return true;
                                            const search = searchGastos.toLowerCase();
                                            return (g.concepto || '').toLowerCase().includes(search) ||
                                                (g.serie || '').toLowerCase().includes(search) ||
                                                (g.numero || '').toLowerCase().includes(search);
                                        });

                                        if (gastosFiltrados.length === 0) {
                                            return (
                                                <div className="text-center py-4 text-muted">
                                                    <i className="fa fa-search fa-2x mb-2"></i>
                                                    <p>No se encontraron gastos con ese criterio</p>
                                                </div>
                                            );
                                        }

                                        return gastosFiltrados.map(egr => {
                                            const searchStr = `${egr.concepto || ''} ${egr.observaciones || ''}`;
                                            const isMultiple = searchStr.includes('[ORIGEN:MULTIPLE]');
                                            const matchExistente = searchStr.match(/\[ORIGEN:([^\]]+)\]/);
                                            const conceptoExistente = isMultiple ? 'MULTIPLE' : (matchExistente ? matchExistente[1] : '');

                                            const valorActual = directExpenseAssign[egr.id] !== undefined
                                                ? directExpenseAssign[egr.id]
                                                : conceptoExistente;

                                            const estaAsignado = !!valorActual;

                                            // [MOD] Parsear Desglose si es múltiple
                                            let breakdownText = '';
                                            if (isMultiple) {
                                                const jsonMatch = searchStr.match(/Desglose:\s*(\{.*\})/);
                                                if (jsonMatch) {
                                                    try {
                                                        const jsonClean = jsonMatch[1].replace(/""/g, '"');
                                                        const obj = JSON.parse(jsonClean);
                                                        breakdownText = Object.entries(obj)
                                                            .map(([k, v]) => `${k.replace('SESSION:', '')}: S/${parseFloat(v).toFixed(2)}`)
                                                            .join(', ');
                                                    } catch (e) {
                                                        console.error('Error parsing breakdown display:', e);
                                                    }
                                                }
                                            }

                                            return (
                                                <div key={egr.id} className="p-3 mb-2 rounded" style={{
                                                    background: estaAsignado
                                                        ? 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))'
                                                        : 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))',
                                                    border: estaAsignado
                                                        ? '2px solid rgba(16,185,129,0.4)'
                                                        : '2px solid rgba(239,68,68,0.4)',
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr auto auto',
                                                    gap: '15px',
                                                    alignItems: 'center'
                                                }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', color: '#ffffff', marginBottom: '4px', textTransform: 'uppercase', fontSize: '0.9rem' }}>
                                                            {egr.concepto}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                                                            <span className="mr-3"><i className="fa fa-file-alt mr-1"></i> {egr.tipo_comprobante} {egr.serie}-{egr.numero}</span>
                                                            <span><i className="fa fa-calendar-alt mr-1"></i> {egr.fecha_gasto}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        fontSize: '1.2rem',
                                                        color: '#ffffff',
                                                        minWidth: '100px',
                                                        textAlign: 'right',
                                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                    }}>
                                                        S/ {parseFloat(egr.monto).toFixed(2)}
                                                    </div>
                                                    <div className="d-flex flex-column align-items-end">
                                                        <select
                                                            className="form-control"
                                                            style={{
                                                                width: '200px',
                                                                background: estaAsignado ? '#d1fae5' : '#fee2e2',
                                                                fontWeight: 'bold',
                                                                border: estaAsignado ? '2px solid #10b981' : '2px solid #ef4444',
                                                                opacity: !analysis?.sesion_id ? 0.7 : 1,
                                                                cursor: !analysis?.sesion_id ? 'not-allowed' : 'pointer'
                                                            }}
                                                            value={valorActual}
                                                            onChange={(e) => handleDirectExpenseChange(egr.id, e.target.value)}
                                                            disabled={!analysis?.sesion_id || valorActual === 'MULTIPLE'}
                                                            title={breakdownText || ''}
                                                        >
                                                            <option value="">(Sin asignar)</option>
                                                            {valorActual === 'MULTIPLE' && <option value="MULTIPLE">(Múltiple / Desglose)</option>}
                                                            {analysis.grupos.map(g => (
                                                                <option key={g.key} value={g.key}>{g.label}</option>
                                                            ))}
                                                        </select>
                                                        {breakdownText && (
                                                            <small style={{
                                                                fontSize: '0.75rem',
                                                                color: '#fff',
                                                                marginTop: '4px',
                                                                maxWidth: '200px',
                                                                textAlign: 'right',
                                                                lineHeight: '1.2'
                                                            }}>
                                                                {breakdownText}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                {/* Botones de Acción */}
                                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            setIsGastosModalOpen(false);
                                            setSearchGastos('');
                                        }}
                                    >
                                        <i className="fa fa-times mr-2"></i> Cerrar
                                    </button>

                                    {Object.keys(directExpenseAssign).length > 0 && analysis?.sesion_id && (
                                        <button
                                            className="btn btn-success btn-lg"
                                            onClick={async () => {
                                                await saveDirectExpenseAssignments();
                                                setIsGastosModalOpen(false);
                                            }}
                                            disabled={processing}
                                        >
                                            {processing ? (
                                                <><i className="fa fa-spinner fa-spin mr-2"></i> Guardando...</>
                                            ) : (
                                                <><i className="fa fa-save mr-2"></i> Guardar {Object.values(directExpenseAssign).filter(v => v).length} Asignación(es)</>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal de Depósito Individual */}
            {depositModal.isOpen && (
                <div className="reg-modal-overlay">
                    <div className="reg-modal" style={{ maxWidth: '500px' }}>
                        <div className="reg-modal-header" style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
                            <h3 className="mb-0"><i className="fa fa-university mr-2"></i> Registrar Depósito</h3>
                            <button className="close-btn border-0 bg-transparent text-white" onClick={() => setDepositModal({ isOpen: false, group: null, amount: '', accountId: '' })}>×</button>
                        </div>
                        <div className="reg-modal-body p-4">
                            <div className="mb-4 text-center">
                                <div className="text-muted small">CONCEPTO</div>
                                <h4 className="font-weight-bold">{depositModal.group?.label}</h4>
                                <div className="badge badge-light-success px-3 py-2 mt-2" style={{ fontSize: '1rem' }}>
                                    Saldo Disponible: S/ {depositModal.group?.pending.toFixed(2)}
                                </div>
                            </div>

                            <div className="form-group mb-3">
                                <label className="filter-label">Cuenta Bancaria Destino</label>
                                <select
                                    className="form-control form-control-lg"
                                    value={depositModal.accountId}
                                    onChange={(e) => setDepositModal(prev => ({ ...prev, accountId: e.target.value }))}
                                >
                                    <option value="">Seleccione una cuenta...</option>
                                    {analysis.cuentas.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.banco} - {c.moneda} - {c.numero_cuenta}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group mb-4">
                                <label className="filter-label">Monto a Depositar</label>
                                <div className="input-group input-group-lg">
                                    <span className="input-group-text">S/</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={depositModal.amount}
                                        onChange={(e) => setDepositModal(prev => ({ ...prev, amount: e.target.value }))}
                                        placeholder="0.00"
                                        max={depositModal.group?.pending}
                                    />
                                </div>
                                <small className="text-muted">El monto no puede exceder el saldo disponible.</small>
                            </div>

                            <div className="d-flex gap-2 mt-4">
                                <button
                                    className="btn btn-outline-secondary btn-lg flex-grow-1"
                                    onClick={() => setDepositModal({ isOpen: false, group: null, amount: '', accountId: '' })}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-success btn-lg flex-grow-2"
                                    style={{ flex: 2 }}
                                    disabled={processing || !depositModal.accountId || !depositModal.amount || parseFloat(depositModal.amount) <= 0}
                                    onClick={() => executeDeposit(depositModal.group, {
                                        amount: depositModal.amount,
                                        accountId: depositModal.accountId
                                    })}
                                >
                                    {processing ? <i className="fa fa-spinner fa-spin mr-2"></i> : <i className="fa fa-check-circle mr-2"></i>}
                                    Confirmar Depósito
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Detalle por Concepto */}
            {detailsModal.isOpen && detailsModal.group && (
                <div
                    className="modal-overlay"
                    onClick={() => setDetailsModal({ isOpen: false, group: null, movimientos: [] })}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.6)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <div
                        className="modal-content-custom shadow-lg"
                        onClick={e => e.stopPropagation()}
                        style={{
                            maxWidth: '850px',
                            width: '90%',
                            background: '#ffffff',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div className="p-4" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    <i className={getIconForConcept(detailsModal.group.label)} style={{ color: '#38bdf8' }}></i>
                                    {detailsModal.group.label}
                                </h4>
                                <small className="text-muted">Detalle de Recaudación, Egresos y Cuadre de Concepto</small>
                            </div>
                            <button
                                className="btn-close btn-close-white"
                                onClick={() => setDetailsModal({ isOpen: false, group: null, movimientos: [] })}
                                style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                            />
                        </div>

                        <div className="p-4" style={{ overflowY: 'auto' }}>
                            {/* Cards Summary */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <div className="p-3 rounded-3" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                                        <small className="text-muted d-block font-weight-bold">RECAUDADO</small>
                                        <span className="h4 font-weight-bold" style={{ color: '#10b981' }}>+ S/ {detailsModal.group.total.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 rounded-3" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                                        <small className="text-muted d-block font-weight-bold">EGRESOS</small>
                                        <span className="h4 font-weight-bold" style={{ color: '#ef4444' }}>- S/ {(detailsModal.group.egresos || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 rounded-3" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                                        <small className="text-muted d-block font-weight-bold">DEPÓSITOS</small>
                                        <span className="h4 font-weight-bold" style={{ color: '#f59e0b' }}>- S/ {(detailsModal.group.deposited || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="p-3 rounded-3" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                                        <small className="text-muted d-block font-weight-bold">DISPONIBLE</small>
                                        <span className="h4 font-weight-bold text-primary">S/ {detailsModal.group.pending.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Movimientos Table */}
                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <i className="fa fa-list text-primary"></i> Movimientos Registrados en este Turno
                            </h5>

                            {detailsModal.movimientos.length === 0 ? (
                                <div className="text-center py-4 text-muted bg-light rounded-3">
                                    <i className="fa fa-info-circle fa-2x mb-2 text-secondary"></i>
                                    <p className="mb-0">No se encontraron movimientos registrados directamente para este concepto en la sesión actual.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-striped align-middle">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Tipo / Origen</th>
                                                <th>Descripción / Detalle</th>
                                                <th>Fecha</th>
                                                <th className="text-end">Monto</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailsModal.movimientos.map(mov => (
                                                <tr key={mov.id}>
                                                    <td>
                                                        <span className={`badge ${mov.es_salida ? 'bg-danger' : 'bg-success'}`}>
                                                            {mov.tipo || 'PAGO'}
                                                        </span>
                                                    </td>
                                                    <td>{mov.descripcion}</td>
                                                    <td>{new Date(mov.fecha).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                                                    <td className={`text-end font-weight-bold ${mov.es_salida ? 'text-danger' : 'text-success'}`}>
                                                        {mov.es_salida ? '-' : '+'} S/ {mov.monto.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="p-3 bg-light border-top d-flex justify-content-end">
                            <button
                                className="btn btn-secondary px-4 btn-pill"
                                onClick={() => setDetailsModal({ isOpen: false, group: null, movimientos: [] })}
                            >
                                Cerrar Detalle
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const getIconForConcept = (label) => {
    if (!label) return 'fa fa-box';
    const l = label.toUpperCase();
    if (l.includes('TRANSFERENCIA') || l.includes('HABILITACION') || l.includes('RECIBID') || l.includes('(TRANSF)')) return 'fa fa-exchange-alt';
    if (l.includes('MATRICULA')) return 'fa fa-user-plus';
    if (l.includes('PENSION')) return 'fa fa-graduation-cap';
    if (l.includes('MATERIAL')) return 'fa fa-book';
    if (l.includes('CUOTA')) return 'fa fa-calendar-check';
    if (l.includes('AGENDA')) return 'fa fa-address-book';
    return 'fa fa-money-bill-alt';
};

export default ArqueoDistribucion;
